"use client";
import { useCallback } from "react";
import type { JobItemDto, ResumeUploadSource } from "@artha/api-contracts";
import {
  RESUME_UPLOAD_MAX_BYTES,
  RESUME_UPLOAD_MIME_TYPES,
  type ResumeUploadMimeType,
} from "@artha/api-contracts";
import { writeCandidateIdentity } from "../../services/storage";
import { uploadResumeForPersonalization } from "../../services/personalizationClient";

import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

import type { CandidatePersonalizationState } from "../../personalization.types";
export function useCandidateResumePersonalization(
  creatorId: string | undefined,
  state: CandidatePersonalizationState,
) {
  const {
    t,
    identity,
    setIdentity,
    setEditableProfile,
    hasResume,
    setHasResume,
    setIsUploading,
    setError,
    capturePersonalizationEvent,
    getPosthogDistinctId,
  } = state;
  const uploadResume = useCallback(
    async (
      file: File,
      source: ResumeUploadSource = "jobs_page",
      job?: Pick<JobItemDto, "id" | "ref_id">,
    ) => {
      if (!creatorId) throw new Error(t("errors.creatorUnavailable"));
      if (
        !RESUME_UPLOAD_MIME_TYPES.includes(file.type as ResumeUploadMimeType) ||
        file.size > RESUME_UPLOAD_MAX_BYTES
      ) {
        const message = t("errors.unsupportedResume");
        setError(message);
        throw new Error(message);
      }

      setIsUploading(true);
      setError(null);
      try {
        const result = await uploadResumeForPersonalization({
          file,
          creatorId,
          posthogDistinctId: getPosthogDistinctId(),
          externalId: identity.externalId || undefined,
          source,
          jobId: job?.id,
          refId: job?.ref_id || undefined,
        });
        if (result.parserStatus !== "success" || !result.editableProfile) {
          throw new Error(t("errors.resumeUnreadable"));
        }
        const nextIdentity = {
          ...identity,
          externalId: result.externalId,
        };
        setIdentity(nextIdentity);
        writeCandidateIdentity(nextIdentity);
        setEditableProfile(result.editableProfile);
        setHasResume(true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_UPLOAD, {
          file_type: file.type,
          replacing_resume: hasResume,
          source,
        });
        return result;
      } catch (uploadError) {
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : t("errors.resumeUploadFailed");
        setError(message);
        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [
      capturePersonalizationEvent,
      creatorId,
      getPosthogDistinctId,
      hasResume,
      identity,
      t,
    ],
  );

  return uploadResume;
}
